import React from 'react';
import { Container, Row, Col, Card, Badge, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faChartLine,
    faExclamationTriangle, faLightbulb, faRocket, faArrowRight,
    faDesktop, faServer, faDatabase, faChartBar, faRandom,
    faStore, faClock, faMapMarkerAlt
} from '@fortawesome/free-solid-svg-icons';
import { Link } from 'react-router-dom';
import SEO from '../SEO/SEO';

import pyramid from "../../assets/images/use-cases/Customer-Flow-Process.png";
import vis from "../../assets/images/use-cases/Customer-Flow-Dashboard.png";

const CustomerFlowOptimisation = () => {
    return (
        <>
            <SEO
                title="In-Store Customer Flow Optimisation: AI Digital Twin for Retail | Nebulyx AI"
                description="Optimise retail store layouts using AI Digital Twins. Model customer movement patterns, simulate product placement changes, and increase revenue per square foot without physical disruption."
                keywords="AI digital twin, retail analytics, customer flow, store layout optimisation, heat map, dwell time, conversion rate, product placement"
                canonicalUrl="https://nebulyx.ai/use-cases/customer-flow-optimisation"
                ogType="article"
            />

            <article className="use-case-page">
                {/* Hero */}
                <header className="use-case-hero">
                    <Container>
                        <Row>
                            <Col className={"w-100"}>
                                <div className={"w-100 text-center"}>
                                    <div >
                                        <Badge className="sector-badge m-1 mb-4 h-100">
                                            <FontAwesomeIcon icon={faStore}/>
                                            &nbsp;
                                            Retail
                                        </Badge>
                                        <h1 className="use-case-title">Question:&nbsp;How can we increase revenue per square foot by 15% without changing store layout?</h1>
                                    </div>
                                </div>
                                <div>
                                    <p className="use-case-subtitle w-100 text-center">
                                        Model customer movement patterns using traffic sensors and POS data, simulating product placement changes to optimise flow and maximise engagement in high-margin zones.
                                    </p>
                                </div>
                            </Col>
                        </Row>

                        <Row className="hero-stats-inline ">
                            <Col className="stat-item-inline">
                                <FontAwesomeIcon icon={faChartLine} className="stat-icon"/>
                                <span className="stat-value">15%</span>
                                <span className="stat-label">Revenue per Sq Ft</span>
                            </Col>
                            <Col  className="stat-item-inline">
                                <FontAwesomeIcon icon={faClock} className="stat-icon"/>
                                <span className="stat-value">+23%</span>
                                <span className="stat-label">Dwell Time in Premium Zones</span>
                            </Col>
                            <Col  className="stat-item-inline">
                                <FontAwesomeIcon icon={faMapMarkerAlt} className="stat-icon"/>
                                <span className="stat-value">Zero</span>
                                <span className="stat-label">Physical Store Disruption</span>
                            </Col>
                        </Row>

                    </Container>
                </header>

                {/* Executive Summary */}
                <section className="exec-summary-section">
                    <Container>
                        <h2 className="section-title">Executive Summary</h2>
                        <Row className="g-4">
                            <Col md={4}>
                                <Card className="summary-card h-100">
                                    <Card.Body>
                                        <h3 className={"text-start"}><span className={"card-icon-wrapper problem me-1"}><FontAwesomeIcon
                                            sty icon={faExclamationTriangle}/></span>The Problem</h3>
                                        <ul className="summary-list">
                                            <li>High-margin products placed in low-traffic zones</li>
                                            <li>No visibility into actual customer movement patterns</li>
                                            <li>Layout changes are costly and disruptive to test</li>
                                            <li>Promotional displays positioned based on intuition, not data</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="summary-card h-100">
                                    <Card.Body>
                                        <h3 className={"text-start"}><span
                                            className={"card-icon-wrapper solution me-1"}><FontAwesomeIcon
                                            sty icon={faLightbulb}/></span>The Solution</h3>
                                        <ul className="summary-list">
                                            <li>Ingest real-time traffic sensors and POS transaction data</li>
                                            <li>Build a Data Object Graph of customer paths and dwell times</li>
                                            <li>Simulate placement changes before physical implementation</li>
                                            <li>Predictive model forecasts traffic from layout modifications</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="summary-card h-100">
                                    <Card.Body>
                                        <h3 className={"text-start"}><span
                                            className={"card-icon-wrapper benefits me-1"}><FontAwesomeIcon
                                            sty icon={faRocket}/></span>The Benefits</h3>
                                        <ul className="summary-list">
                                            <li>Visual heat maps showing projected traffic improvements</li>
                                            <li>Optimal placement recommendations for high-margin products</li>
                                            <li>Increased engagement in premium sections</li>
                                            <li>15% revenue uplift without store disruption</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    </Container>
                </section>

                {/* Process Steps */}
                <section className="content-section process-section">
                    <Container>
                        <h2 className="section-title">The AI Digital Twin Process</h2>
                        <p className="text-center text-muted mb-4">
                            Ingests real-time traffic sensor data, POS transactions, and store blueprints to build a Data Object Graph that maps customer journeys and enables virtual layout testing.
                        </p>
                        <div className="process-steps-container">
                            <div className="process-step-item">
                                <div className="step-number">1</div>
                                <div className="step-content">
                                    <strong>Capture Movement</strong>
                                    <span>Traffic sensors, POS transactions, store layout blueprints</span>
                                </div>
                            </div>
                            <div className="step-arrow">
                                <FontAwesomeIcon icon={faArrowRight}/>
                            </div>
                            <div className="process-step-item">
                                <div className="step-number">2</div>
                                <div className="step-content">
                                    <strong>Build Graph</strong>
                                    <span>Map customer paths, dwell times, and conversion rates by zone</span>
                                </div>
                            </div>
                            <div className="step-arrow">
                                <FontAwesomeIcon icon={faArrowRight}/>
                            </div>
                            <div className="process-step-item">
                                <div className="step-number">3</div>
                                <div className="step-content">
                                    <strong>Simulate Changes</strong>
                                    <span>Test product placement and promotional display positions virtually</span>
                                </div>
                            </div>
                            <div className="step-arrow">
                                <FontAwesomeIcon icon={faArrowRight}/>
                            </div>
                            <div className="process-step-item">
                                <div className="step-number">4</div>
                                <div className="step-content">
                                    <strong>Optimise & Deploy</strong>
                                    <span>Implement winning configurations with predicted revenue impact</span>
                                </div>
                            </div>
                        </div>
                    </Container>
                </section>

                {/* AI Digital Twin - Two Sections */}
                <section className="content-section twin-section">
                    <Container>
                        <h2 className="section-title">The AI Digital Twin</h2>
                        <Row className="g-4">
                            {/* Back-end Architecture */}
                            <Col lg={6}>
                                <Card className="twin-view-card h-100">
                                    <Card.Body>
                                        <div className="twin-view-header">
                                            <div className="twin-view-icon backend">
                                                <FontAwesomeIcon icon={faServer}/>
                                            </div>
                                            <h3>Digital Twin Process Flow</h3>
                                        </div>
                                        <p className="twin-view-description">
                                            Ingests data from in-store traffic sensors, point-of-sale systems, and store layout
                                            blueprints to model customer movement patterns end-to-end. Graph analysis identifies
                                            high-traffic corridors, dead zones, and optimal product placement opportunities.</p>
                                        <div className="image-parent">
                                            <img className={"image-child-span w-100 h-100"} src={pyramid} alt=""/>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>

                            {/* Front-end UI */}
                            <Col lg={6}>
                                <Card className="twin-view-card h-100">
                                    <Card.Body>
                                        <div className="twin-view-header">
                                            <div className="twin-view-icon frontend">
                                                <FontAwesomeIcon icon={faDesktop}/>
                                            </div>
                                            <h3>Flow Optimisation Dashboard</h3>
                                        </div>
                                        <p className="twin-view-description">
                                            Displays interactive heat maps of customer traffic, dwell time analysis by zone,
                                            and conversion rate metrics. Allows merchandising teams to simulate placement changes
                                            and preview projected revenue impact before physical implementation.</p>
                                        <div className="image-parent">
                                            <img className={"image-child-span w-100 h-100"} src={vis} alt=""/>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>


                        {/* Twin Components */}
                        <Row className="g-4 mt-4">
                            <Col md={4}>
                                <Card className="twin-card h-100">
                                    <Card.Body>
                                        <div className="twin-card-header">
                                            <div className="twin-icon">
                                                <FontAwesomeIcon icon={faDatabase}/>
                                            </div>
                                            <h4>Data Analysed</h4>
                                        </div>
                                        <ul className="twin-list">
                                            <li>In-Store Traffic Sensor Data</li>
                                            <li>POS Transaction Records</li>
                                            <li>Store Layout Blueprints</li>
                                            <li>Product Category Margins</li>
                                            <li>Promotional Calendar Events</li>
                                            <li>Staff Positioning Data</li>
                                            <li>Time-of-Day Traffic Patterns</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="twin-card h-100">
                                    <Card.Body>
                                        <div className="twin-card-header">
                                            <div className="twin-icon">
                                                <FontAwesomeIcon icon={faChartBar}/>
                                            </div>
                                            <h4>AI / Data Products</h4>
                                        </div>
                                        <ul className="twin-list">
                                            <li>Customer movement pattern tracker</li>
                                            <li>Dwell time analyser by section</li>
                                            <li>Conversion rate calculator by zone</li>
                                            <li>Traffic prediction model</li>
                                            <li>Placement recommendation engine</li>
                                            <li>Revenue impact simulator</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="twin-card h-100">
                                    <Card.Body>
                                        <div className="twin-card-header">
                                            <div className="twin-icon">
                                                <FontAwesomeIcon icon={faRandom}/>
                                            </div>
                                            <h4>Scenarios Tested</h4>
                                        </div>
                                        <ul className="twin-list">
                                            <li>High-margin product repositioning</li>
                                            <li>Promotional display placement</li>
                                            <li>Seasonal layout adjustments</li>
                                            <li>End-cap optimisation</li>
                                            <li>Checkout queue flow management</li>
                                            <li>Cross-selling zone creation</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>

                        {/* Discovery */}
                        <div className="discovery-highlight mt-5">
                            <Row className="align-items-center">
                                <Col lg={8}>
                                    <h3>The Discovery</h3>
                                    <p>
                                        By modelling customer flow as a <strong>Data Object Graph</strong>, we can reveal
                                        hidden patterns in how shoppers navigate the store and where they spend time.
                                        <strong> Simulation can identify optimal placement for high-margin products that increases
                                            dwell time in premium sections by 23%</strong>
                                        {" "}without any physical store disruption. The visual heat map shows exactly where
                                        to position promotional displays for maximum engagement and conversion.
                                    </p>
                                </Col>
                                <Col lg={4}>
                                    <div className="discovery-stat">
                                        <span className="discovery-label">Optimal Strategy Found</span>
                                        <span className="discovery-value">Zone Rebalancing</span>
                                        <span className="discovery-note">15% revenue lift, zero disruption</span>
                                    </div>
                                </Col>
                            </Row>
                        </div>
                    </Container>
                </section>

                {/* CTA */}
                <section className="cta-section">
                    <Container>
                        <div className="cta-content">
                            <h2>Want to Optimise Your Store Layout?</h2>
                            <p>
                                If you're placing products based on intuition rather than data,
                                let's build your customer flow Digital Twin together.
                            </p>
                            <div className="cta-buttons">
                                <Button
                                    variant="light"
                                    size="lg"
                                    href="https://cal.eu/jon-cooke/discovery-call"
                                    className="me-3 mb-2"
                                >
                                    Book a Discovery Call
                                    <FontAwesomeIcon icon={faArrowRight} className="ms-2"/>
                                </Button>
                                <Link to="/use-cases">
                                    <Button variant="outline-light" size="lg" className="mb-2">
                                        View More Use Cases
                                    </Button>
                                </Link>
                            </div>
                        </div>
                    </Container>
                </section>
            </article>
        </>
    );
};

export default CustomerFlowOptimisation;